import React from 'react';
import SignaturePad from './SignaturePad';
import { buildApiUrl } from '../../api/client';
import type { WorkOrderFormSection, WorkOrderReportAnswer } from '../../lib/workOrderForm';
import { setAnswerValue } from '../../lib/workOrderForm';

type Props = {
  sections: WorkOrderFormSection[];
  answers: WorkOrderReportAnswer[];
  disabled?: boolean;
  onChange: (answers: WorkOrderReportAnswer[]) => void;
  onUploadFile?: (file: File) => Promise<string>;
};

async function defaultUpload(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(buildApiUrl('/api/upload'), {
    method: 'POST',
    credentials: 'include',
    body: form
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Upload failed');
  return json.data?.url || '';
}

function answerValue(answers: WorkOrderReportAnswer[], sectionKey: string, fieldId: string) {
  return answers.find((a) => a.sectionKey === sectionKey && a.fieldId === fieldId)?.value;
}

export default function WorkOrderDynamicForm({
  sections,
  answers,
  disabled,
  onChange,
  onUploadFile = defaultUpload
}: Props): React.JSX.Element {
  function update(sectionKey: string, fieldId: string, value: unknown) {
    onChange(setAnswerValue(answers, sectionKey, fieldId, value));
  }

  return (
    <div className="wo-dynamic-form">
      {sections.map((section) => (
        <section key={section.key} className="wo-dynamic-section">
          <h3>{section.title}</h3>
          {section.fields.map((field) => {
            const val = answerValue(answers, section.key, field.fieldId);
            return (
              <div key={field.fieldId} className="wo-dynamic-field">
                <label className="wo-dynamic-label">{field.label}</label>
                {field.type === 'short_text' ? (
                  <input
                    className="wo-dynamic-input"
                    disabled={disabled}
                    placeholder={field.placeholder}
                    value={typeof val === 'string' ? val : ''}
                    onChange={(e) => update(section.key, field.fieldId, e.target.value)}
                  />
                ) : null}
                {field.type === 'long_text' ? (
                  <textarea
                    className="wo-dynamic-textarea"
                    disabled={disabled}
                    placeholder={field.placeholder}
                    value={typeof val === 'string' ? val : ''}
                    onChange={(e) => update(section.key, field.fieldId, e.target.value)}
                  />
                ) : null}
                {field.type === 'number' ? (
                  <input
                    type="number"
                    className="wo-dynamic-input"
                    disabled={disabled}
                    value={typeof val === 'number' ? val : ''}
                    onChange={(e) => update(section.key, field.fieldId, e.target.value === '' ? undefined : Number(e.target.value))}
                  />
                ) : null}
                {field.type === 'yes_no' ? (
                  <div className="wo-yesno-row" role="radiogroup" aria-label={field.label}>
                    {['Yes', 'No'].map((opt) => {
                      const active = val === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          disabled={disabled}
                          onClick={() => update(section.key, field.fieldId, active ? undefined : opt)}
                          className={[
                            'wo-yesno-btn',
                            `wo-yesno-btn--${opt.toLowerCase()}`,
                            active ? 'wo-yesno-btn--active' : ''
                          ].filter(Boolean).join(' ')}
                        >
                          <span className="wo-yesno-btn__icon">{opt === 'Yes' ? '✓' : '✗'}</span>
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {field.type === 'select' ? (
                  <select
                    className="wo-dynamic-select"
                    disabled={disabled}
                    value={typeof val === 'string' ? val : ''}
                    onChange={(e) => update(section.key, field.fieldId, e.target.value)}
                  >
                    <option value="">Select…</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : null}
                {field.type === 'multi_select' ? (
                  <div>
                    {(field.options || []).map((opt) => {
                      const selected = Array.isArray(val) ? val.includes(opt) : false;
                      return (
                        <label key={opt} className="wo-check-row">
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={selected}
                            onChange={(e) => {
                              const current = Array.isArray(val) ? [...val] : [];
                              const next = e.target.checked ? [...current, opt] : current.filter((v) => v !== opt);
                              update(section.key, field.fieldId, next);
                            }}
                          />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}
                {field.type === 'date' ? (
                  <input
                    type="date"
                    className="wo-dynamic-input"
                    disabled={disabled}
                    value={typeof val === 'string' ? val.slice(0, 10) : ''}
                    onChange={(e) => update(section.key, field.fieldId, e.target.value)}
                  />
                ) : null}
                {field.type === 'time' ? (
                  <input
                    type="time"
                    className="wo-dynamic-input"
                    disabled={disabled}
                    value={typeof val === 'string' ? val : ''}
                    onChange={(e) => update(section.key, field.fieldId, e.target.value)}
                  />
                ) : null}
                {field.type === 'datetime' ? (
                  <input
                    type="datetime-local"
                    className="wo-dynamic-input"
                    disabled={disabled}
                    value={typeof val === 'string' ? val : ''}
                    onChange={(e) => update(section.key, field.fieldId, e.target.value)}
                  />
                ) : null}
                {field.type === 'signature' ? (
                  disabled ? (
                    typeof val === 'string' && val ? <img src={buildApiUrl(String(val))} alt="Signature" className="wo-photo-preview" /> : <p className="wo-dynamic-required">No signature</p>
                  ) : (
                    <SignaturePad
                      value={typeof val === 'string' ? val : ''}
                      onChange={async (dataUrl) => {
                        if (!dataUrl) {
                          update(section.key, field.fieldId, '');
                          return;
                        }
                        const blob = await fetch(dataUrl).then((r) => r.blob());
                        const file = new File([blob], 'signature.png', { type: 'image/png' });
                        const url = await onUploadFile(file);
                        update(section.key, field.fieldId, url);
                      }}
                    />
                  )
                ) : null}
                {field.type === 'photo' ? (
                  <div>
                    {typeof val === 'string' && val ? (
                      <img src={buildApiUrl(val)} alt={field.label} className="wo-photo-preview" />
                    ) : null}
                    {!disabled ? (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const url = await onUploadFile(file);
                          update(section.key, field.fieldId, url);
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}
                {field.helpText ? <p className="wo-dynamic-required">{field.helpText}</p> : null}
                {field.required ? <p className="wo-dynamic-required">Required</p> : null}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
